import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { createUserWithIdp, notifyAccountSetup } from '../util/api';
import { clearCfOrder, readCfOrder } from '../util/cfOrder';
import { resetVisitorIdentity } from '../util/visitorLink';

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

// ================ Initial State ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

// ================ Async Thunks ================ //

const authInfoThunk = createAsyncThunk('auth/authInfo', (_, thunkAPI) => {
  const { extra: sdk } = thunkAPI;
  return sdk.authInfo().catch(e => {
    // Requesting auth info just reads the token from the token
    // store (i.e. cookies), and should not fail in normal
    // circumstances. If it fails, it's due to a programming
    // error. In that case we mark the operation done and dispatch
    // `null` success action that marks the user as unauthenticated.
    log.error(e, 'auth-info-failed');
    return null;
  });
});

const loginThunk = createAsyncThunk(
  'auth/login',
  ({ username, password }, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    return sdk
      .login({ username, password })
      .then(() => {
        return dispatch(fetchCurrentUser({ afterLogin: true }));
      })
      .then(() => ({ username, password }))
      .catch(e => {
        // Story 9.18 AC2 — a failed login used to log nothing at all, so a
        // login outage was invisible in Sentry. EXACTLY TWO ARGUMENTS: the
        // third argument of log.error is forwarded verbatim to Sentry as issue
        // "extra" (util/log.js:385-393), and this path has no non-PII
        // correlation key the way signup does — no username, no password.
        log.error(e, 'login-failed');
        return rejectWithValue(storableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'loginInProgress')) {
        return false;
      }
    },
  }
);

const logoutThunk = createAsyncThunk(
  'auth/logout',
  (_, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    return sdk
      .logout()
      .then(() => {
        dispatch(clearCurrentUser());
        log.clearUserId();
        // Story 7.19 (Task 10) → ticket 88 Part 1 (ledger #1376): logout
        // starts a FRESH visitor. The durable tph_visitor_session_id, its
        // owner marker and every tph_visitor_linked:* flag are removed, so the
        // next sign-in on this browser — same or different account — gets a
        // new visitor id and never merges with the one that just signed out.
        resetVisitorIdentity();
        return true;
      })
      .catch(e => rejectWithValue(storableError(e)));
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'logoutInProgress')) {
        return false;
      }
    },
  }
);

const signupThunk = createAsyncThunk(
  'auth/signup',
  (params, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    // Story 3.10 AC6.2 — the discriminator. The single `.catch` below used to
    // log 'signup-failed' for BOTH "the account was never created" and "the
    // account WAS created and everything after it fell over" — which are
    // opposite facts about the customer's money. The second case means we have
    // a paying customer sitting on an account with no credits; the first means
    // we simply lost a signup. This flag is the difference.
    let accountCreated = false;
    // Correlation key for the .catch below. The Sharetribe user uuid is not
    // PII and is what every server-side log (tph-api, heldGrantSweep) already
    // keys on, so it — and nothing about the person — is what we report.
    // Only ever populated in the post-create case; absent means no account.
    let createdUserId = null;

    return sdk.currentUser
      .create(params)
      .then(res => {
        accountCreated = true;
        createdUserId = res?.data?.data?.id?.uuid || null;
        return res;
      })
      .then(() =>
        dispatch(loginThunk({ username: params.email, password: params.password })).unwrap()
      )
      // Story 3.9 (AC4): account-setup convergence notify — attaches any held
      // slot grants + checkout T&C BEFORE the post-signup redirect, so the
      // onboarding wizard already sees credits/tos. AWAITED but never throws
      // (bounded server-side; on failure signup proceeds).
      //
      // ⚠️ THIS CHAIN IS THE SINGLE POINT OF FAILURE Story 3.10 was written
      // about. It is chained behind sdk.login() and an .unwrap()-ed
      // fetchCurrentUser: if EITHER rejects, the chain jumps straight to the
      // .catch below — AFTER the account and session are already valid — and
      // notifyAccountSetup() is never called. tph-api's `*/5` heldGrantSweep
      // cron is what actually guarantees the grant converges; this call is
      // only for immediacy.
      // Story 3.11 (AC1.4/AC1.6): the captured ClickFunnels order id rides
      // this notify, then is cleared once the call SETTLES either way — the
      // account exists regardless, and a stale id must not ride a different
      // signup later in the same session.
      .then(() =>
        notifyAccountSetup(readCfOrder()).then(
          result => {
            clearCfOrder();
            return result;
          },
          err => {
            clearCfOrder();
            throw err;
          }
        )
      )
      .then(() => params)
      .catch(e => {
        // Story 3.10 AC6.4 — hashed-never-raw. This payload is forwarded
        // verbatim to Sentry as issue "extra" (see util/log.js), so it carries
        // NO customer email / firstName / lastName. The uuid below is the only
        // identifier, and it is what server-side logs already key on.
        const userIdMaybe = createdUserId ? { userId: createdUserId } : {};
        log.error(e, accountCreated ? 'signup-post-create-failed' : 'signup-failed', {
          ...userIdMaybe,
          // True ⇒ the account EXISTS. Anything the customer paid for is at
          // risk of being stranded; the tph-api sweep should pick it up within
          // five minutes.
          accountCreated,
        });
        return rejectWithValue(storableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'signupInProgress')) {
        return false;
      }
    },
  }
);

const signupWithIdpThunk = createAsyncThunk(
  'auth/signupWithIdp',
  (params, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    // Story 3.11 (AC1.5/AC1.6): the IdP route fires the convergence notify
    // server-side, so the order id has to travel in the create body.
    //
    // ⚠️ Review S-3 — CLEARED ON RESOLVE ONLY, deliberately asymmetric, and
    // this now MATCHES the password path above rather than contradicting it.
    // AC1.6's justification for clearing is "the account exists either way",
    // and on this path that is true only of the resolve branch:
    // `createUserWithIdp` rejects on ANY non-2xx — email already taken, an
    // expired IdP token, a network fault — and in every one of those cases no
    // account was created and the buyer will retry. Clearing there would make
    // `readCfOrder()` return null on the retry and drop them back to
    // email-match: stranded, in exactly the mismatched-email case this story
    // exists to serve. The password path gets this for free (a failed
    // `sdk.currentUser.create` never enters its `.then` chain); here it has to
    // be written. A failed signup KEEPS the id.
    const cfOrder = readCfOrder();
    const cfOrderMaybe = cfOrder ? { cfOrder } : {};
    return createUserWithIdp({ ...params, ...cfOrderMaybe })
      .then(res => {
        clearCfOrder();
        return res;
      })
      .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
      .then(() => params)
      .catch(e => {
        // Story 3.10 AC6.4 — hashed-never-raw. `params` is the whole IdP
        // confirm-form submission: idpToken (a LIVE CREDENTIAL), any edited
        // email / firstName / lastName, displayName, and
        // protectedData.phoneNumber. log.error forwards its data object to
        // Sentry verbatim as issue "extra" (see util/log.js), so none of that
        // may be logged — only the provider and the account role, neither of
        // which identifies the person.
        //
        // No Sharetribe uuid is available on this path, so per the
        // omit-never-substitute rule there is no correlation id at all:
        // createUserWithIdp resolves with the loginWithIdp auth response
        // rather than the created user resource, and in the case where
        // fetchCurrentUser is what rejected, the currentUser entity never
        // reached the store either.
        const idpIdMaybe = params?.idpId ? { idpId: params.idpId } : {};
        const userTypeMaybe = params?.publicData?.userType
          ? { userType: params.publicData.userType }
          : {};
        log.error(e, 'create-user-with-idp-failed', {
          ...idpIdMaybe,
          ...userTypeMaybe,
        });
        return rejectWithValue(storableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'confirmInProgress')) {
        return false;
      }
    },
  }
);

// ================ Slice ================ //

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Auth Info
    builder.addCase(authInfoThunk.fulfilled, (state, action) => {
      const payload = action.payload;
      state.authInfoLoaded = true;
      state.isAuthenticated = authenticated(payload);
      state.isLoggedInAs = loggedInAs(payload);
      state.authScopes = payload?.scopes || [];
    });

    // Login
    builder
      .addCase(loginThunk.pending, state => {
        state.loginInProgress = true;
        state.loginError = null;
        state.logoutError = null;
        state.signupError = null;
      })
      .addCase(loginThunk.fulfilled, state => {
        state.loginInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loginInProgress = false;
        state.loginError = action.payload;
      });

    // Logout
    builder
      .addCase(logoutThunk.pending, state => {
        state.logoutInProgress = true;
        state.loginError = null;
        state.logoutError = null;
      })
      .addCase(logoutThunk.fulfilled, state => {
        state.logoutInProgress = false;
        state.isAuthenticated = false;
        state.isLoggedInAs = false;
        state.authScopes = [];
      })
      .addCase(logoutThunk.rejected, (state, action) => {
        state.logoutInProgress = false;
        state.logoutError = action.payload;
      });

    // Signup
    builder
      .addCase(signupThunk.pending, state => {
        state.signupInProgress = true;
        state.loginError = null;
        state.signupError = null;
      })
      .addCase(signupThunk.fulfilled, state => {
        state.signupInProgress = false;
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.signupInProgress = false;
        state.signupError = action.payload;
      });

    // Signup with IDP (Confirm)
    builder
      .addCase(signupWithIdpThunk.pending, state => {
        state.confirmInProgress = true;
        state.loginError = null;
        state.confirmError = null;
      })
      .addCase(signupWithIdpThunk.fulfilled, state => {
        state.confirmInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(signupWithIdpThunk.rejected, (state, action) => {
        state.confirmInProgress = false;
        state.confirmError = action.payload;
      });
  },
});

export { logoutThunk };
export default authSlice.reducer;

// ================ Selectors ================ //

export const authenticationInProgress = (state, nextInProgress = 'any') => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.auth;
  const anyInProgress =
    loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
  return nextInProgress === 'loginInProgress'
    ? loginInProgress || logoutInProgress || confirmInProgress
    : anyInProgress;
};

// ================ Thunk Wrappers ================ //
// These maintain the same API as the original thunks

export const login = (username, password) => dispatch => {
  return dispatch(loginThunk({ username, password })).unwrap();
};

export const logout = () => dispatch => {
  return dispatch(logoutThunk()).unwrap();
};

export const signup = params => dispatch => {
  return dispatch(signupThunk(params)).unwrap();
};

export const signupWithIdp = params => dispatch => {
  return dispatch(signupWithIdpThunk(params)).unwrap();
};

export const authInfo = () => dispatch => {
  return dispatch(authInfoThunk()).unwrap();
};
